import { Component, inject } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth/auth.service';
import { SidebarComponent } from './sidebar/sidebar.component';
import { NonProductionBannerComponent } from './non-production-banner/non-production-banner.component';
import { PropertyAssessmentService } from './property/property-assessment.service';
import { COUNTY_COPY } from './county/county-config';
import { SignupAddressStore } from './signup/address/signup-address.store';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, SidebarComponent, NonProductionBannerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {
  private auth = inject(AuthService);
  private router = inject(Router);
  private assessment = inject(PropertyAssessmentService);
  private signupAddress = inject(SignupAddressStore);

  showSidebar = false;
  showFooter = false;
  /** On /signup the footer shows to everyone with the matched sign-up county's copy, centered (no right-hand column). */
  signupFooter = false;

  /**
   * Regulatory footer copy for the currently-known property's county.
   *
   * AppComponent is the root shell, not scoped to a single property, so there's no
   * guarantee `assessment.data` is populated yet when the footer renders (e.g. right
   * after login before the assessment call resolves) — defaults to `'harris'`, which
   * preserves current (Texas-only) behavior for any route/timing where county isn't
   * yet resolvable.
   *
   * Deliberately still sourced from `COUNTY_COPY`, not the backend's `complianceCopy`:
   * the backend's `regulatoryFooter` DTO only carries `stateAgencyName`/`licenseeName`/
   * `licenseNumber` — it's missing the full compliance text (mailing address, phone
   * numbers, website) this footer legally needs to show for Harris. Swapping to the
   * backend value today would silently drop that text. Revisit once the backend DTO
   * carries the full disclosure, not just the agency/license identifiers.
   */
  get footerCopy(): string {
    const county = this.signupFooter ? this.signupAddress.displayCounty() : this.assessment.data?.property?.countyCode ?? 'harris';
    return COUNTY_COPY[county].regulatoryFooter;
  }

  /** The public sign-up flow (/signup → /enrolled, or /too-many-requests) never shows it, even to a stale session. */
  private readonly noSidebarRoutes = ['/login', '/verify', '/signup', '/enrolled', '/too-many-requests'];
  private readonly footerRoutes = ['/confirm', '/sign', '/your-case'];
  /** Shown whether or not the visitor is logged in. */
  private readonly publicFooterRoutes = ['/signup'];

  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        const url = e.urlAfterRedirects;
        this.showSidebar =
          this.auth.isLoggedIn() &&
          !this.noSidebarRoutes.some(r => url.startsWith(r));
        this.signupFooter = this.publicFooterRoutes.some(r => isRoute(url, r));
        this.showFooter = this.signupFooter ||
          (this.auth.isLoggedIn() && this.footerRoutes.some(r => isRoute(url, r)));
      });
  }
}

/** True when `url` is `route` or below it; a bare prefix check would match '/signup' for '/sign'. */
function isRoute(url: string, route: string): boolean {
  const path = url.split(/[?#]/)[0];
  return path === route || path.startsWith(route + '/');
}
